/** @format */

import React from 'react'

export const MISSING = (
  <>
    <svg
      className="h-5 w-5 text-gray-400"
      x-description="Heroicon name: minus"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
        clipRule="evenodd"
      ></path>
    </svg>
    <span className="sr-only">Not included</span>
  </>
)

export const CHECKBOX = (
  <>
    <svg
      className="h-5 w-5 text-green-500"
      x-description="Heroicon name: check"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      ></path>
    </svg>
    <span className="sr-only">Yes</span>
  </>
)

const infinityMark = className => (
  <>
    <svg
      className={className}
      enableBackground="new 0 0 100 47.084"
      viewBox="-3 -3 106 53.084"
      y="0px"
      x="0px"
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      fill="currentColor"
      width="22px"
      height="22px"
    >
      <path
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinejoin="round"
        d="M76.459,0C65.343,0,55.149,10.439,50,16.753C44.85,10.439,34.657,0,23.541,0C10.56,0,0,10.561,0,23.542
	s10.56,23.542,23.541,23.542C34.657,47.084,44.85,36.645,50,30.33c5.148,6.314,15.342,16.754,26.459,16.754
	c12.98,0,23.541-10.561,23.541-23.542S89.439,0,76.459,0z M23.541,38.909c-8.473,0-15.367-6.895-15.367-15.367
	c0-8.474,6.894-15.368,15.367-15.368c7.895,0,16.97,9.623,21.403,15.368C40.51,29.289,31.436,38.909,23.541,38.909z M76.459,38.909
	c-7.897,0-16.975-9.627-21.406-15.372c4.423-5.746,13.482-15.365,21.406-15.365c8.474,0,15.366,6.894,15.366,15.368
	C91.825,32.015,84.933,38.909,76.459,38.909z"
      ></path>
    </svg>
  </>
)

export const INFINITY = infinityMark('inline')

//TODO: for AB testing, check cookie to determine whether or not to show free column
export const SHOW_FREE = false
export const STEPS = {
  SALES: -2,
  CANCEL: -1,
  NOTHING: 0,
  REGISTER: 1,
  PERSONAL: 2,
  PAYMENT: 3,
}

// What the plans page displays, and in what order. Separation of concerns:
//
//   - Functionality: what each plan includes, defined in formspree/plans.py
//   - Marketing/Sales Presentation: how we describe the plans to customers,
//     defined below.
//
// Ids refer to feature_defs[].id
// Anything not listed here simply isn't shown.
// constants.test.jsx asserts every id below actually exists.

// Top of the plan "cards" at the top of the page
export const CARD_LIMITS = [
  'account_monthly_submissions',
  'team_members',
  // removed--doesn't differentiate plans:
  //'forms',
  //'projects',
]

// Bottom of the plan "cards" at the top of the page.
// Each column shows only the entries cheaper columns haven't already shown,
// so this order also decides which column a given feature appears in.
export const CARD_FEATURES = [
  // (commented out items are an attempt to limit to 3 points per card,
  // for easier readability)
  'plugins',
  //'export',
  'thankyou',
  'basic_spam_filter',
  'premium_plugins',
  'autoresponses',
  //'spamfilter',
  'api_access',
  'custom_emails',
  //'custom_domains',
  'submission_routing',
  'priority_support',
  'sla',
  'onboarding',
  'phone_support',
]

// Anchor targets for the two comparison sections, shared by each section
// wrapper and the jump links between them.
export const FEATURE_SECTION_ID = 'features'
export const PLUGIN_SECTION_ID = 'plugins'

// Presentation-only tweaks applied wherever a feature is displayed
// (e.g. top cards and feature table).
export const FEATURE_DISPLAY_OVERRIDES = {
  account_monthly_submissions: {
    // cards only — the comparison table row keeps the fuller
    // "Monthly Submissions"
    suffix: '/mo',
  },
  // "1 Team Members" was ambiguous — is that one teammate, or just you?
  // So the plans page presents it as the number of teammates you can add.
  // 'Unlimited' passes through unadjusted.
  team_members: {
    name: 'Additional Team Members',
    // The cards are too narrow for the full name — it wraps and pushes that
    // card's button out of line. '+' carries the "additional" meaning instead.
    shortname: 'Team Members',
    singularShortname: 'Team Member',
    prefix: '+',
    // used where the count comes out at exactly one, as on the
    // Professional card
    singularName: 'Additional Team Member',
    adjustLimit: limit => limit - 1,
  },
  // 'anchor' links the label to a section of the page that expands on it.
  // These point at the plugin table. Only the plan cards at the top of the
  // page render it.
  plugins: {anchor: PLUGIN_SECTION_ID},
  premium_plugins: {anchor: PLUGIN_SECTION_ID},
}

// Plugin row overrides, keyed by kind. Sales page only — the editor and
// backend still use the catalog.
//
// Stripe: works on any plan, but it's test-only below Professional.
// Auto Response: no logo of its own, so borrow email's.
export const PLUGIN_DISPLAY_OVERRIDES = {
  autoresponse: {logoKind: 'email'},
}

// The feature comparison table, as ordered sections of ordered rows.
export const FEATURE_TABLE_SECTIONS = [
  [
    'Limits',
    [
      'account_monthly_submissions',
      'file_uploads',
      'retention',
      'forms',
      // Probably not important for driving sales.
      // Instead, added a bit of copy under "forms" explaining that you can
      // organize your forms into projects.
      //'projects',
    ],
  ],
  [
    'Custom Branding',
    ['thankyou', 'custom_emails', 'custom_domains', 'trusted'],
  ],
  [
    'Collaboration and Teams',
    ['linked_addresses', 'multiple_to_addresses', 'team_members'],
  ],
  // Removing this section for now because we didn't think these features
  // were driving sales.
  // Moved 'retention' to the top 'Limits' section, but note that 'export'
  // and 'search_submissions' are currently not listed at all.
  //['Data Management', ['retention', 'export', 'search_submissions']],
  [
    'Automation and APIs',
    [
      'ajax',
      // Removed for brevity, and because this distinction is now described
      // in the plugins table at the bottom of the page. Still, there might
      // be an argument for including these rows and linking them down to
      // that table.
      //'plugins',
      //'premium_plugins',
      'api_access',
      'autoresponses',
      'submission_routing',
    ],
  ],
  [
    'Privacy and Spam',
    [
      'dashboard',
      'own_recaptcha',
      'domain_whitelist',
      'basic_spam_filter',
      'spamfilter',
      'custom_honeypot',
      'spam_rules',
    ],
  ],
  [
    'Service and Support',
    ['priority_support', 'phone_support', 'sla', 'onboarding'],
  ],
]

// The plugin comparison table, in the same shape as FEATURE_TABLE_SECTIONS
// above. Ordered sections of ordered rows.
//
// The plugin 'ids' refer to PLUGIN_CONFIGS[].kind in
// formspree/js/plugins/pluginConfigs.ts
//
// Note that 'group' from pluginConfig.ts is ignored here (but used in the
// form editor today). So we're free to tweak the sell on the Plans page by
// re-grouping and re-ordering the plugins.
//
// constants.test.jsx asserts every id/'kind' below exists, appears once,
// and that nothing in the catalog is left unlisted. So a new plugin fails
// a test until someone decides where it goes.
export const PLUGIN_TABLE_SECTIONS = [
  // 'autoresponse' already in the feature table--can maybe remove here
  ['Email Plugins', ['email', 'autoresponse']],
  ['Notification / Chat Plugins', ['discord', 'slack', 'telegram']],
  [
    'Productivity Plugins',
    [
      'airtable',
      'github',
      'google-sheets',
      'notion',
      'trello',
      'linear',
      'asana',
    ],
  ],
  ['Payment Processor Plugins', ['stripe_v2']],
  [
    'CRM Plugins',
    ['hubspot', 'activecampaign', 'pipedrive', 'salesforce', 'zohocrm'],
  ],
  [
    'Newsletter Plugins',
    ['mailchimp', 'mailerlite', 'convertkit', 'klaviyo', 'constantcontact'],
  ],
  ['Support Plugins', ['freshdesk', 'gladly', 'gorgias', 'zendesk']],
  ['Automation Plugins', ['zapier', 'webhook']],
]

// Subtitles under each plan heading in the plugin table, in place of the
// product description — on this table what matters is which tier of plugins
// you get. Keyed by product id; a plan with no entry keeps its description.
export const PLUGIN_TABLE_SUBTITLES = {
  // free gets Stripe, Email, Discord, Slack and Telegram — 5 of the 32 rows
  free: 'Just email and chat',
  personal: 'Basic plugins',
  professional: 'All premium plugins',
  business: 'All premium plugins',
  custom: 'All premium plugins',
}
